import React, { forwardRef, useEffect, useRef, useState } from 'react';
import TextInput, { TextInputProps } from '../TextInput';

const ALLOWED_PERCENTAGE_CHARACTERS_REGEX = /[^0-9.]/g;

export const extractNumberFromString = (value: string) =>
  value.replace(ALLOWED_PERCENTAGE_CHARACTERS_REGEX, '');

export interface PercentageInputProps extends Omit<TextInputProps, 'onBlur'> {
  onBlur: (value: number | string) => void;
  hideDecimals?: boolean;
}

const PercentageInput = forwardRef<HTMLInputElement, PercentageInputProps>((props, ref) => {
  const percent = useRef<number>(0);
  const [displayValue, setDisplayValue] = useState<string>();

  const handleRounding = (value: number) => {
    return props.hideDecimals ? Math.round(value) : value;
  };

  const formatPercentage = (val: number) => {
    let formattedValue;

    if (props.hideDecimals) {
      formattedValue = val.toString();
    } else {
      formattedValue = val.toFixed(2);
    }

    return `${formattedValue}%`;
  };

  useEffect(() => {
    if (props.value || props.value === 0) {
      percent.current = Number(props.value);
      setDisplayValue(formatPercentage(handleRounding(percent.current)));
    } else {
      percent.current = 0;
      setDisplayValue('');
    }
  }, [props.value]);

  const onValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = extractNumberFromString(evt.target.value);

    percent.current = handleRounding(Number(value || 0));
    setDisplayValue(value);
  };

  const onValueBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
    if (evt.target.value) {
      const value = extractNumberFromString(evt.target.value);
      percent.current = handleRounding(Number(value));
      setDisplayValue(formatPercentage(handleRounding(percent.current || 0)));
      props.onBlur(percent.current);
    } else {
      props.onBlur('');
    }
  };

  return (
    <TextInput
      {...props}
      onChange={onValueChange}
      onFocus={onValueChange}
      onBlur={onValueBlur}
      value={displayValue}
      ref={ref}
    />
  );
});

export default PercentageInput;
